import type { DocumentAudience } from '@/blocks/domain/helpers/lineItemsColumnKeys';
import { parsePriceBookBuildRows, type PriceBookBuildRow, type PriceBookRowKind, type PriceBookSubsectionKey } from './priceBookRowModel';
import {
  PRICE_BOOK_SUBSECTIONS,
  formatPriceBookMoney,
  priceBookSheetColumnLabel,
  type PriceBookMoneyKey,
  type PriceBookSheetColumnKey
} from './priceBookMoneyLabels';

export interface PriceBookSheetColumn {
  key: PriceBookSheetColumnKey;
  label: string;
  align: 'left' | 'right';
}

export interface PriceBookSheetCell {
  key: PriceBookSheetColumnKey;
  text: string;
  align: 'left' | 'right';
  emphasis: boolean;
}

export interface PriceBookSheetRowView {
  id: string;
  rowKind: PriceBookRowKind;
  name: string;
  nameEditable: boolean;
  hidden: boolean;
  dimmed: boolean;
  cells: PriceBookSheetCell[];
  /** Raw numeric values - the canvas edits these, never the formatted text. */
  values: Readonly<Record<PriceBookMoneyKey, number | null>>;
  /** Position of this row in the block's flattened printable row list (the slice index). */
  flatIndex: number;
}

export interface PriceBookSubsectionView {
  key: PriceBookSubsectionKey;
  label: string;
  columns: PriceBookSheetColumn[];
  rows: PriceBookSheetRowView[];
}

export interface PriceBookSheetView {
  sheetId: string;
  sheetName: string;
  heading: string;
  flatNote: string;
  hidden: boolean;
  dimmed: boolean;
  subsections: PriceBookSubsectionView[];
}

export interface PriceBookBySheetView {
  sheets: PriceBookSheetView[];
  /** Printable rows across every sheet - drives `splittableBlocksRowCounts`. */
  rowCount: number;
}

export interface PriceBookBySheetOptions {
  audience: DocumentAudience;
  currency: string;
  cultureTag: string;
}

const MONEY_KEYS: readonly PriceBookMoneyKey[] = ['regular', 'ot1', 'ot2'];

function buildColumns(subsection: PriceBookSubsectionKey, rows: readonly PriceBookBuildRow[]): PriceBookSheetColumn[] {
  const moneyKeys = MONEY_KEYS.filter((key) => key === 'regular' || rows.some((row) => row[key] !== null));
  const keys: PriceBookSheetColumnKey[] = ['name', 'unit', ...moneyKeys];
  return keys.map((key) => ({
    key,
    label: priceBookSheetColumnLabel(subsection, key),
    align: key === 'name' || key === 'unit' ? 'left' : 'right'
  }));
}

function buildCell(row: PriceBookBuildRow, column: PriceBookSheetColumn, options: PriceBookBySheetOptions): PriceBookSheetCell {
  if (column.key === 'name') return { key: 'name', text: row.name, align: 'left', emphasis: true };
  if (column.key === 'unit') return { key: 'unit', text: row.unit, align: 'left', emphasis: false };
  return {
    key: column.key,
    text: formatPriceBookMoney(row[column.key], options.currency, options.cultureTag),
    align: 'right',
    emphasis: false
  };
}

function isRowVisible(row: PriceBookBuildRow, audience: DocumentAudience): boolean {
  if (audience === 'internal') return true;
  return !row.hidden && !row.sheetHidden;
}

function groupBySheet(rows: readonly PriceBookBuildRow[]): PriceBookBuildRow[][] {
  const groups = new Map<string, PriceBookBuildRow[]>();
  for (const row of rows) {
    const list = groups.get(row.sheetId) ?? [];
    list.push(row);
    groups.set(row.sheetId, list);
  }
  return [...groups.values()].sort((left, right) => (left[0]?.sheetIndex ?? 0) - (right[0]?.sheetIndex ?? 0));
}

function sheetHeading(first: PriceBookBuildRow): string {
  return first.sheetJobType ? `${first.sheetName} · ${first.sheetJobType}` : first.sheetName;
}

/**
 * Groups the seeded rows by sheet (in `sheet_index` order), then into the four fixed subsections.
 * Customer audience drops hidden sheets and hidden rows; internal audience keeps them, dimmed.
 */
export function buildPriceBookBySheetView(rawRows: readonly unknown[] | undefined, options: PriceBookBySheetOptions): PriceBookBySheetView {
  const visible = parsePriceBookBuildRows(rawRows).filter((row) => isRowVisible(row, options.audience));
  let flatIndex = 0;
  const sheets: PriceBookSheetView[] = [];

  for (const sheetRows of groupBySheet(visible)) {
    const first = sheetRows[0];
    if (!first) continue;
    const subsections: PriceBookSubsectionView[] = [];
    for (const definition of PRICE_BOOK_SUBSECTIONS) {
      const subsectionRows = sheetRows.filter((row) => row.subsection === definition.key);
      if (subsectionRows.length === 0) continue;
      const columns = buildColumns(definition.key, subsectionRows);
      subsections.push({
        key: definition.key,
        label: definition.label,
        columns,
        rows: subsectionRows.map((row) => ({
          id: row.id,
          rowKind: row.rowKind,
          name: row.name,
          nameEditable: row.nameEditable,
          hidden: row.hidden,
          dimmed: row.hidden || row.sheetHidden,
          cells: columns.map((column) => buildCell(row, column, options)),
          values: { regular: row.regular, ot1: row.ot1, ot2: row.ot2 },
          flatIndex: flatIndex++
        }))
      });
    }
    sheets.push({
      sheetId: first.sheetId,
      sheetName: first.sheetName,
      heading: sheetHeading(first),
      flatNote: first.sheetFlatNote,
      hidden: first.sheetHidden,
      dimmed: first.sheetHidden,
      subsections
    });
  }

  return { sheets, rowCount: flatIndex };
}

export interface PriceBookSliceRange {
  startIndex: number;
  endIndex: number;
}

/** Keeps only the rows whose flat index falls in the slice; sheets and subsections left empty drop out. */
export function slicePriceBookBySheetView(view: PriceBookBySheetView, slice: PriceBookSliceRange | undefined): PriceBookSheetView[] {
  if (!slice) return view.sheets;
  const inSlice = (row: PriceBookSheetRowView) => row.flatIndex >= slice.startIndex && row.flatIndex < slice.endIndex;
  return view.sheets.flatMap((sheet) => {
    const subsections = sheet.subsections.flatMap((subsection) => {
      const rows = subsection.rows.filter(inSlice);
      return rows.length > 0 ? [{ ...subsection, rows }] : [];
    });
    return subsections.length > 0 ? [{ ...sheet, subsections }] : [];
  });
}

/** Row ids of a sheet's custom line items, in display order - the line-item reorder domain. */
export function priceBookLineItemIds(sheet: PriceBookSheetView): string[] {
  return sheet.subsections.flatMap((subsection) => subsection.rows.filter((row) => row.rowKind === 'lineItem').map((row) => row.id));
}
